import { css } from '@emotion/react';

import { KAI_SHIKI_TYPOGRAPHY, styleguideColors, styleguideFontSize, styleguideFontWeight } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { styleguideFontFamily, styleguideRadius, styleguideSpacing } from '@kaiizen/kai-shiki/core';

export const TURN_BASED_DASHBOARD_CONTROLS_STYLES = {
  toolbar: css`
    label: TURN_BASED_DASHBOARD_TOOLBAR;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    gap: ${styleguideSpacing.s3}px;
    padding-bottom: ${styleguideSpacing.s3}px;
    border-bottom: 1px solid ${styleguideColors.stroke};

    @media (max-width: 720px) {
      grid-template-columns: 1fr;
      align-items: flex-start;
    }
  `,
  toolbarPrimary: css`
    label: TURN_BASED_DASHBOARD_TOOLBAR_PRIMARY;
    display: flex;
    align-items: center;
    gap: ${styleguideSpacing.s2}px;
    flex-wrap: wrap;
  `,
  projectViewTab: css`
    label: TURN_BASED_DASHBOARD_PROJECT_VIEW_TAB;
    display: inline-flex;
    align-items: center;
    gap: ${styleguideSpacing.s1}px;
    min-height: calc(${styleguideSpacing.s5}px + ${styleguideSpacing.s1}px);
    padding: 0 ${styleguideSpacing.s2}px;
    border: 1px solid ${styleguideColors.stroke};
    border-radius: 0;
    background: ${styleguideColors.paper};
    color: ${styleguideColors.textSecondary};
    cursor: pointer;
    font-family: ${styleguideFontFamily.mono};
    font-size: ${pxToRem(styleguideFontSize.monoCaption)};
    font-weight: ${styleguideFontWeight.bold};
    text-transform: uppercase;

    &:hover {
      background: ${styleguideColors.wash};
    }
  `,
  projectViewTabs: css`
    label: TURN_BASED_DASHBOARD_PROJECT_VIEW_TABS;
    display: flex;
    align-items: stretch;
  `,
  projectViewTabConnected: css`
    label: TURN_BASED_DASHBOARD_PROJECT_VIEW_TAB_CONNECTED;
    border-left: 0;
  `,
  projectViewTabSelected: css`
    label: TURN_BASED_DASHBOARD_PROJECT_VIEW_TAB_SELECTED;
    background: ${styleguideColors.ink};
    border-color: ${styleguideColors.ink};
    color: ${styleguideColors.white};

    &:hover {
      background: ${styleguideColors.ink};
    }
  `,
  filterMenuRoot: css`
    label: TURN_BASED_DASHBOARD_FILTER_MENU_ROOT;
    position: relative;
  `,
  filterButton: css`
    label: TURN_BASED_DASHBOARD_FILTER_BUTTON;
    display: inline-flex;
    align-items: center;
    gap: ${styleguideSpacing.s1}px;
    min-height: calc(${styleguideSpacing.s5}px + ${styleguideSpacing.s1}px);
    padding: 0 ${styleguideSpacing.s2}px;
    border: 1px solid ${styleguideColors.stroke};
    border-radius: 0;
    background: ${styleguideColors.paper};
    color: ${styleguideColors.ink};
    cursor: pointer;
    font-family: ${styleguideFontFamily.mono};
    font-size: ${pxToRem(styleguideFontSize.monoCaption)};
    font-weight: ${styleguideFontWeight.bold};
  `,
  filterButtonSelected: css`
    label: TURN_BASED_DASHBOARD_FILTER_BUTTON_SELECTED;
    background: ${styleguideColors.wash};
  `,
  dropdownCaret: css`
    label: TURN_BASED_DASHBOARD_DROPDOWN_CARET;
    display: inline-flex;
    transform: rotate(90deg);
    transition: transform 120ms ease;
  `,
  dropdownCaretExpanded: css`
    label: TURN_BASED_DASHBOARD_DROPDOWN_CARET_EXPANDED;
    transform: rotate(-90deg);
  `,
  pastDue: css`
    label: TURN_BASED_DASHBOARD_PAST_DUE;
    color: ${styleguideColors.error};
    ${KAI_SHIKI_TYPOGRAPHY.smallBold}
  `,
  toolbarActions: css`
    label: TURN_BASED_DASHBOARD_TOOLBAR_ACTIONS;
    display: flex;
    align-items: center;
    gap: ${styleguideSpacing.s2}px;

    @media (max-width: 720px) {
      width: 100%;
    }
  `,
  searchButton: css`
    label: TURN_BASED_DASHBOARD_SEARCH_BUTTON;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: calc(${styleguideSpacing.s5}px + ${styleguideSpacing.s1}px);
    height: calc(${styleguideSpacing.s5}px + ${styleguideSpacing.s1}px);
    border: 1px solid ${styleguideColors.stroke};
    border-radius: 0;
    background: ${styleguideColors.paper};
    color: ${styleguideColors.ink};
    cursor: pointer;
  `,
  search: css`
    label: TURN_BASED_DASHBOARD_SEARCH;
    position: relative;
    width: calc(${styleguideSpacing.s8}px * 3);
  `,
  searchIcon: css`
    label: TURN_BASED_DASHBOARD_SEARCH_ICON;
    position: absolute;
    z-index: 1;
    top: 50%;
    left: ${styleguideSpacing.s2}px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    transform: translateY(-50%);
  `,
  searchInput: css`
    label: TURN_BASED_DASHBOARD_SEARCH_INPUT;
    width: 100%;
    height: calc(${styleguideSpacing.s5}px + ${styleguideSpacing.s1}px);
    box-sizing: border-box;
    padding: 0 calc(${styleguideSpacing.s5}px + ${styleguideSpacing.s1}px) 0 ${styleguideSpacing.s6}px;
    border: 1px solid ${styleguideColors.ink};
    border-radius: 0;
    background: ${styleguideColors.paper};
    color: ${styleguideColors.ink};
    ${KAI_SHIKI_TYPOGRAPHY.small}

    &::placeholder {
      color: ${styleguideColors.textTertiary};
    }
  `,
  searchClearButton: css`
    label: TURN_BASED_DASHBOARD_SEARCH_CLEAR_BUTTON;
    position: absolute;
    top: 50%;
    right: ${styleguideSpacing.s1}px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: ${styleguideSpacing.s4}px;
    height: ${styleguideSpacing.s4}px;
    padding: 0;
    border: 0;
    background: transparent;
    cursor: pointer;
    transform: translateY(-50%);
  `,
  sort: css`
    label: TURN_BASED_DASHBOARD_SORT;
    position: relative;
    width: calc(${styleguideSpacing.s8}px * 2 + ${styleguideSpacing.s6}px + ${styleguideSpacing.s3}px);
  `,
  sortButton: css`
    label: TURN_BASED_DASHBOARD_SORT_BUTTON;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${styleguideSpacing.s1}px;
    width: 100%;
    height: calc(${styleguideSpacing.s5}px + ${styleguideSpacing.s1}px);
    padding: 0 ${styleguideSpacing.s2}px;
    border: 1px solid ${styleguideColors.stroke};
    border-radius: 0;
    background: ${styleguideColors.paper};
    color: ${styleguideColors.ink};
    cursor: pointer;
    font-family: ${styleguideFontFamily.mono};
    font-size: ${pxToRem(styleguideFontSize.monoCaption)};
    font-weight: ${styleguideFontWeight.bold};
    text-transform: uppercase;
  `,
  sortMenu: css`
    label: TURN_BASED_DASHBOARD_SORT_MENU;
    position: absolute;
    z-index: 10;
    top: calc(100% + ${styleguideSpacing.s1}px);
    right: 0;
    width: 100%;
    padding: ${styleguideSpacing.s1}px 0;
    border: 1px solid ${styleguideColors.stroke};
    background: ${styleguideColors.paper};
    box-shadow: 0 ${styleguideSpacing.s1}px ${styleguideSpacing.s3}px ${styleguideColors.strokeFaded};
  `,
  sortMenuOption: css`
    label: TURN_BASED_DASHBOARD_SORT_MENU_OPTION;
    display: flex;
    align-items: center;
    width: 100%;
    min-height: ${styleguideSpacing.s5}px;
    padding: 0 ${styleguideSpacing.s2}px;
    border: 0;
    background: transparent;
    color: ${styleguideColors.ink};
    cursor: pointer;
    text-align: left;
    ${KAI_SHIKI_TYPOGRAPHY.small}

    &:hover {
      background: ${styleguideColors.wash};
    }
  `,
  sortMenuOptionSelected: css`
    label: TURN_BASED_DASHBOARD_SORT_MENU_OPTION_SELECTED;
    background: ${styleguideColors.selectedWash};
    font-weight: ${styleguideFontWeight.bold};
  `,
  filterMenu: css`
    label: TURN_BASED_DASHBOARD_FILTER_MENU;
    position: absolute;
    z-index: 10;
    top: calc(100% + ${styleguideSpacing.s1}px);
    left: 0;
    width: calc(${styleguideSpacing.s8}px * 4 + ${styleguideSpacing.s5}px + ${styleguideSpacing.s3}px + ${styleguideSpacing.s2}px);
    padding: ${styleguideSpacing.s4}px;
    background: ${styleguideColors.paper};
    border: 1px solid ${styleguideColors.stroke};
    box-shadow: 0 ${styleguideSpacing.s1}px ${styleguideSpacing.s3}px ${styleguideColors.strokeFaded};
  `,
  filterHeading: css`
    label: TURN_BASED_DASHBOARD_FILTER_HEADING;
    margin: 0 0 ${styleguideSpacing.s3}px;
    color: ${styleguideColors.ink};
    ${KAI_SHIKI_TYPOGRAPHY.mediumBold}
  `,
  filterGroup: css`
    label: TURN_BASED_DASHBOARD_FILTER_GROUP;
    padding: ${styleguideSpacing.s3}px 0;
    border-top: 1px solid ${styleguideColors.stroke};
  `,
  filterGroupFirst: css`
    label: TURN_BASED_DASHBOARD_FILTER_GROUP_FIRST;
    padding-top: 0;
    border-top: 0;
  `,
  filterGroupLabel: css`
    label: TURN_BASED_DASHBOARD_FILTER_GROUP_LABEL;
    margin: 0 0 ${styleguideSpacing.s1}px;
    color: ${styleguideColors.textTertiary};
    font-family: ${styleguideFontFamily.mono};
    font-size: ${pxToRem(styleguideFontSize.monoCaption)};
    font-weight: ${styleguideFontWeight.bold};
    text-transform: uppercase;
  `,
  filterOption: css`
    label: TURN_BASED_DASHBOARD_FILTER_OPTION;
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-height: ${styleguideSpacing.s6}px;
    padding: 0;
    border: 0;
    background: transparent;
    color: ${styleguideColors.ink};
    cursor: pointer;
    text-align: left;
    ${KAI_SHIKI_TYPOGRAPHY.xSmall}

    &:hover {
      color: ${styleguideColors.info};
    }
  `,
  filterOptionSelected: css`
    label: TURN_BASED_DASHBOARD_FILTER_OPTION_SELECTED;
    color: ${styleguideColors.info};
    font-weight: ${styleguideFontWeight.bold};
  `,
  filterCount: css`
    label: TURN_BASED_DASHBOARD_FILTER_COUNT;
    color: ${styleguideColors.textTertiary};
  `,
  filterStatusLabel: css`
    label: TURN_BASED_DASHBOARD_FILTER_STATUS_LABEL;
    display: inline-flex;
    align-items: center;
    gap: ${styleguideSpacing.s2}px;
  `,
  filterStatusDot: css`
    label: TURN_BASED_DASHBOARD_FILTER_STATUS_DOT;
    width: ${styleguideSpacing.s2}px;
    height: ${styleguideSpacing.s2}px;
    border-radius: ${styleguideRadius.circle};
  `,
  filterStatusDotApproved: css`
    label: TURN_BASED_DASHBOARD_FILTER_STATUS_DOT_APPROVED;
    background: ${styleguideColors.success};
  `,
  filterStatusDotInProgress: css`
    label: TURN_BASED_DASHBOARD_FILTER_STATUS_DOT_IN_PROGRESS;
    background: ${styleguideColors.ink};
  `,
  filterFooter: css`
    label: TURN_BASED_DASHBOARD_FILTER_FOOTER;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: ${styleguideSpacing.s3}px;
    border-top: 1px solid ${styleguideColors.stroke};
    color: ${styleguideColors.textSecondary};
    font-family: ${styleguideFontFamily.mono};
    font-size: ${pxToRem(styleguideFontSize.monoCaption)};
    font-weight: ${styleguideFontWeight.regular};
  `,
  clearFilterButton: css`
    label: TURN_BASED_DASHBOARD_CLEAR_FILTER_BUTTON;
    padding: 0;
    border: 0;
    background: transparent;
    color: ${styleguideColors.textSecondary};
    cursor: pointer;
    font: inherit;
    font-weight: ${styleguideFontWeight.bold};
  `,
};
